/*
 * chart-clock — ONE shared requestAnimationFrame loop for every live chart on the
 * page. The Lit analogue of the agent's ChartAnimationContext: N charts share a
 * single rAF rather than each running its own, and every chart redraws against
 * the SAME `Date.now()` clock so their time axes scroll in lockstep.
 *
 * A chart registers its per-frame redraw in connectedCallback and unregisters in
 * disconnectedCallback; the loop runs only while at least one chart is
 * registered. The smooth-scroll trick lives in the callers: x is computed from
 * `now` every frame (not from sample arrival), so the line slides continuously
 * between samples regardless of sample rate.
 */
type FrameFn = (now: number) => void;

const frames = new Set<FrameFn>();
let rafId = 0;
/** The redraw rate. A 120 Hz display would otherwise draw every chart twice as often for no visible gain. */
const TARGET_FPS = 60;
const FRAME_INTERVAL_MS = 1000 / TARGET_FPS;
let lastFrame = 0;

function tick(rafTime: number): void {
  if (rafTime - lastFrame < FRAME_INTERVAL_MS - 1) {
    rafId = requestAnimationFrame(tick);
    return;
  }
  lastFrame = rafTime;
  const now = Date.now();
  for (const fn of frames) {
    try {
      fn(now);
    } catch {
      // One chart's render error must never kill the shared loop.
    }
  }
  rafId = frames.size > 0 ? requestAnimationFrame(tick) : 0;
}

/** Register a per-frame redraw; returns an unregister fn (call on disconnect). */
export function registerChartFrame(fn: FrameFn): () => void {
  frames.add(fn);
  if (!rafId) rafId = requestAnimationFrame(tick);
  return () => {
    frames.delete(fn);
    if (frames.size === 0 && rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };
}
